import { ref, watch } from 'vue'
import { useDocumentVisibility, useIntervalFn } from '@vueuse/core'

import type { Ref } from 'vue'
import type { RemoteParticipant, Room } from 'livekit-client'
import type { ConnectionEvidence, ConnectionReading, ConnectionUplinkSample } from '@ts-classroom/interfaces'
import type { ConnectionSide } from '@ts-classroom/types'

import { ConnectionTier } from '@ts-classroom/enums'

import { cleanEvidence, useConnectionReceptionLiveKit } from '@hooks/classroom/connection-reception-live-kit.hook'
import { cleanUplink, useConnectionUplinkLiveKit } from '@hooks/classroom/connection-uplink-live-kit.hook'
import { refRoom, useRoomLiveKit } from '@hooks/classroom/room-live-kit.hook'

import { CONNECTION_SAMPLE_INTERVAL_MS } from '@config/connection-quality'
import { createConnectionTierTracker } from '@utils/classroom/connection-tier'

export interface ConnectionTierTransition {
  isSelf: boolean
  tier: ConnectionTier
  previousTier: ConnectionTier
  reading: ConnectionReading
  limitationReason: string | null
  livekitQuality: string | null
  evidence: ConnectionEvidence
}

interface ConnectionTierLiveKitReturn {
  refSelfTier: Ref<ConnectionTier>
  refPeerTier: Ref<ConnectionTier>
  start: (onTransition?: (transition: ConnectionTierTransition) => void) => void
  stop: () => void
}

interface Observed {
  uplink: ConnectionUplinkSample
  evidence: ConnectionEvidence
}

const SIDES: ConnectionSide[] = ['self', 'peer']

const nothingObserved = (): Observed => ({
  uplink: cleanUplink(),
  evidence: { peerLivekitQuality: null, own: cleanEvidence(), peer: null },
})

export const refSelfTier = ref<ConnectionTier>(ConnectionTier.Good)
export const refPeerTier = ref<ConnectionTier>(ConnectionTier.Good)

let transitionHandler: ((transition: ConnectionTierTransition) => void) | null = null
let tracker = createConnectionTierTracker()
let observed = nothingObserved()
let isTicking = false

const forcedTiers: Record<ConnectionSide, ConnectionTier | null> = { self: null, peer: null }

const { remoteName } = useRoomLiveKit()
const visibility = useDocumentVisibility()
const uplink = useConnectionUplinkLiveKit()
const reception = useConnectionReceptionLiveKit()

const tierRefOf = (side: ConnectionSide): Ref<ConnectionTier> => (side === 'self' ? refSelfTier : refPeerTier)

const firstRemote = (room: Room): RemoteParticipant | null => [...room.remoteParticipants.values()][0] ?? null

const resetSide = (side: ConnectionSide): void => {
  tracker.resetSide(side)
  tierRefOf(side).value = ConnectionTier.Good

  if (side === 'peer') reception.reset()
}

const resetBothSides = (): void => {
  tracker = createConnectionTierTracker()
  observed = nothingObserved()
  SIDES.forEach(resetSide)
}

export const setForcedTier = (tier: ConnectionTier | null, side: ConnectionSide | 'both' = 'both'): void => {
  const targets: ConnectionSide[] = side === 'both' ? SIDES : [side]

  targets.forEach((target) => {
    forcedTiers[target] = tier
  })
}

const buildTransition = (
  room: Room,
  side: ConnectionSide,
  tier: ConnectionTier,
  previousTier: ConnectionTier,
): ConnectionTierTransition => {
  const isSelf = side === 'self'
  const { reading, limitationReason } = isSelf ? observed.uplink : cleanUplink()

  return {
    isSelf,
    tier,
    previousTier,
    reading,
    limitationReason,
    livekitQuality: isSelf ? room.localParticipant.connectionQuality : observed.evidence.peerLivekitQuality,
    evidence: observed.evidence,
  }
}

const announceChange = (room: Room, side: ConnectionSide): void => {
  const tier = forcedTiers[side] ?? tracker.current(side)
  const previousTier = tierRefOf(side).value
  if (tier === previousTier) return

  tierRefOf(side).value = tier
  transitionHandler?.(buildTransition(room, side, tier, previousTier))
}

const observe = async (room: Room): Promise<void> => {
  const ownUplink = await uplink.sample(room)
  const verdict = await reception.sample(ownUplink)
  const peer = remoteName.value ? firstRemote(room) : null
  const peerEvidence = reception.peerEvidence()

  observed = {
    uplink: ownUplink,
    evidence: {
      peerLivekitQuality: peer?.connectionQuality ?? null,
      own: reception.ownEvidence(),
      peer: peerEvidence,
    },
  }

  tracker.push({
    at: Date.now(),
    roomState: room.state,
    selfQuality: room.localParticipant.connectionQuality,
    peerQuality: peer?.connectionQuality ?? null,
    isOwnReceptionDegraded: verdict.isDegraded,
    isPeerReceptionDegraded: !!peerEvidence?.isDegraded,
    isPeerMediaStalled: verdict.isStalled,
    isCpuLimited: ownUplink.isCpuLimited || !!peerEvidence?.isCpuLimited,
    isOwnUplinkImpaired: ownUplink.isImpaired,
    isPeerUplinkImpaired: !!peerEvidence?.isUplinkImpaired,
  })

  SIDES.forEach((side) => announceChange(room, side))
}

const tick = async (): Promise<void> => {
  const room = refRoom.value
  if (!room) return
  if (visibility.value !== 'visible') return
  if (isTicking) return

  isTicking = true

  try {
    await observe(room)
  } catch (error) {
    console.error('[classroom] connection tier sampling failed', error)
  } finally {
    isTicking = false
  }
}

const { pause, resume } = useIntervalFn(() => { void tick() }, CONNECTION_SAMPLE_INTERVAL_MS, { immediate: false })

watch(remoteName, (name) => {
  if (name) return

  resetSide('peer')
})

watch(refRoom, (room) => {
  if (room) return

  resetSide('self')
})

export const useConnectionTierLiveKit = (): ConnectionTierLiveKitReturn => {
  const start = (onTransition?: (transition: ConnectionTierTransition) => void): void => {
    transitionHandler = onTransition ?? null
    resetBothSides()
    resume()
  }

  const stop = (): void => {
    pause()
    transitionHandler = null
  }

  return {
    refSelfTier,
    refPeerTier,
    start,
    stop,
  }
}
